import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { ShortenedTextWithCopy } from "components/UI/ShortenedTextWithCopy";
import { ConversionStatusCell } from "components/UI/Table/UI/ConversionStatusCell/ConversionStatusCell.tsx";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { LeadStateCell } from "components/UI/Table/UI/LeadStateCell/LeadStateCell.tsx";
import { UrlCell } from "components/UI/Table/UI/UrlCell/UrlCell.tsx";
import { ConversionStateType } from "types/enums";

import ConversionStateData = App.Data.ConversionStateData;

export const createGeneralInfoList = (
    conversion: ConversionStateData | undefined,
    t: (key: string) => string,
    tenantSlug?: string | null
) => {
    const FORMS_GENERAL_FIELDS = "forms.general-fields";
    const TABLES_GENERAL_COLUMNS = "tables.general-columns";
    const DRAWERS_GENERAL_LABELS = "drawers.general-labels";
    const isClickOrLead =
        conversion?.type === ConversionStateType.Click || conversion?.type === ConversionStateType.Lead;

    return PropertiesList.create(conversion, [
        {
            term: t( `${FORMS_GENERAL_FIELDS}.created_at.label`),
            description: ({ created_at }) => created_at ? <DateTimeCell value={created_at} /> : "-",
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.country.label`),
            description: ({ country }) => country?.name,
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.state.label`),
            description: ({ state }) => (state ? t(`${DRAWERS_GENERAL_LABELS}.ok`) : t(`${DRAWERS_GENERAL_LABELS}.fail`)),
            ...(isClickOrLead && {
                render: ({ state, status }: ConversionStateData) => <LeadStateCell state={state} status={status} />,
            }),
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.status.label`),
            description: ({ status }) => status || "-",
            render: ({ status }: ConversionStateData) => <ConversionStatusCell status={status} />,
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.conversion_type.label`),
            description: ({ type }) => type || "-",
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.advertiser.label`),
            description: ({ advertiser }) => advertiser?.nickname || advertiser?.email || undefined,
            render: ({ advertiser }) => <EntityLinkCell.Advertiser entity={advertiser} />,
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.offer.label`),
            description: ({ offer }) => offer?.title,
            render: ({ offer }) => <EntityLinkCell.Offer entity={offer} />,
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.affiliate.label`),
            description: ({ owner }) => owner?.nickname || owner?.email || undefined,
            render: ({ owner }) => <EntityLinkCell.Owner entity={owner} />,
        },
        {
            term: t(`${TABLES_GENERAL_COLUMNS}.origin_id`),
            description: ({ origin_eid }) => origin_eid,
            render: ({ origin_eid }) => origin_eid ? <ShortenedIdWithCopy id={origin_eid} charactersBetweenDots={35} /> : "-",
        },
        {
            term: t(`${FORMS_GENERAL_FIELDS}.affiliate_cid.label`),
            description: ({ owner_custom_id }) => owner_custom_id || "-",
            render: ({ owner_custom_id }) => (owner_custom_id ? <ShortenedIdWithCopy id={owner_custom_id} charactersBetweenDots={35} /> : "-"),
        },
        ...(conversion?.type === ConversionStateType.Click
            ? [
                  {
                      term: t(`${TABLES_GENERAL_COLUMNS}.gross`),
                      description: ({ gross }: ConversionStateData) => String(gross),
                  },
              ]
            : []),
        ...Array.from({ length: 10 }, (_, i) => i + 1)
            .map((num) => {
                const affKey = `affs${num}`;
                return {
                    term: t(`${TABLES_GENERAL_COLUMNS}.aff_subid${num}`),
                    description: (data: ConversionStateData) => data.owner_data?.[affKey] || undefined,
                    render: (data: ConversionStateData) => {
                        const value = data.owner_data?.[affKey];
                        return value ? <ShortenedTextWithCopy text={String(value)} /> : "-";
                    },
                };
            })
            .filter((_, index) => {
                // Only include if the value exists in the conversion data
                const affKey = `affs${index + 1}`;
                return conversion?.owner_data?.[affKey];
            }),
        ...Array.from({ length: 10 }, (_, i) => i + 1)
            .map((num) => {
                const advKey = `advs${num}`;
                return {
                    term: t(`${TABLES_GENERAL_COLUMNS}.adv_subid${num}`),
                    description: (data: ConversionStateData) => data.advertiser_data?.[advKey] || undefined,
                    render: (data: ConversionStateData) => {
                        const value = data.advertiser_data?.[advKey];
                        return value ? <ShortenedTextWithCopy text={String(value)} /> : "-";
                    },
                };
            })
            .filter((_, index) => {
                // Only include if the value exists in the conversion data
                const advKey = `advs${index + 1}`;
                return conversion?.advertiser_data?.[advKey];
            }),
        ...(tenantSlug && conversion?.type === ConversionStateType.Lead && conversion?.cascadeProcess?.id
            ? [
                  {
                      term: t(`${FORMS_GENERAL_FIELDS}.process_url.label`),
                      description: () => `${import.meta.env.VITE_APP_URL}/${tenantSlug}/processes/${conversion.cascadeProcess!.id}`,
                      render: () => <UrlCell url={`${import.meta.env.VITE_APP_URL}/${tenantSlug}/processes/${conversion.cascadeProcess!.id}`} maxWidth="100%" />,
                  },
              ]
            : []),
    ]);
};
